import React, { useState, useEffect } from 'react';
import './App.css';
import { auth, db, storage } from './firebase';
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import {
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import { collection, onSnapshot, orderBy, query, serverTimestamp, doc, updateDoc, where, deleteDoc, getDoc, setDoc, addDoc, arrayUnion } from "firebase/firestore";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import FinancePage from './components/FinancePage';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initializeAppCheck(db.app, {
      provider: new ReCaptchaV3Provider('6LfWvpcrAAAAAHrvF92hk-Q8hNqLfW0alShamIYs'),
      isTokenAutoRefreshEnabled: true
    });
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      setUser(user);
      setLoading(false);
      if (user) {
        setupNotifications(user);
      }
    });
    return () => unsubscribe();
  }, []);

  const setupNotifications = async (currentUser) => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const messaging = getMessaging();
        const currentToken = await getToken(messaging, {
          vapidKey: 'BEvfoN1VU_m_u4duwuZXASJ_Ear1opWZ2RD45TXzbFcI6TJYc6GhJXDPfNGi9UpBqZi_IHgDrbfI1tKWOg3nkXg',
        });
        if (currentToken) {
          const businessRef = doc(db, "businesses", currentUser.uid);
          await updateDoc(businessRef, { fcmToken: currentToken });
        }
        onMessage(messaging, (payload) => {
            alert(`Nueva Notificación: ${payload.notification.title}\n${payload.notification.body}`);
        });
      }
    } catch (error) {
      console.error('Ocurrió un error al configurar las notificaciones:', error);
    }
  };

  if (loading) {
    return <div className="App"><p>Cargando...</p></div>;
  }

  if (!user) {
    return <LoginPage />;
  }

  return <MainApp user={user} />;
}

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      setError('');
    } catch (error) {
      setError('Usuario o contraseña incorrectos.');
    }
  };


  return (
    <div className="App">
      <div className="login-container">
        <img src="/Logo2.png" alt="Logo Repuestos Link Ve" style={{ width: '225px', display: 'block', margin: '0 auto 20px', borderRadius: '15px' }} />
        <h2>Bienvenido al Panel de Empresas</h2>
        <p>Inicia sesión para administrar las solicitudes</p>
        <form>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Correo electrónico" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" />
          {error && <p style={{ color: '#dc3545', fontSize: '14px' }}>{error}</p>}
          <button onClick={handleLogin}>Iniciar Sesión</button>
        </form>
      </div>
    </div>
  );
};

const MainApp = ({ user }) => {
  // Leemos la página guardada para que no regrese a 'new' si el usuario estaba en otra
  const [page, setPage] = useState(sessionStorage.getItem('currentPage') || 'new');
  const [selectedItem, setSelectedItem] = useState(null);

  // EFECTO DE RECUPERACIÓN DE SCROLL: Se dispara cuando cerramos un detalle (selectedItem vuelve a null)
  useEffect(() => {
    if (!selectedItem) {
      const savedScroll = sessionStorage.getItem('lastScrollPos');
      if (savedScroll) {
        // Timeout de 50ms para dar tiempo a que la lista se renderice antes de mover el scroll
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedScroll));
        }, 50);
      }
    }
  }, [selectedItem]);

  const handleLogout = async () => {
    sessionStorage.clear(); // Limpiamos memoria al salir
    await signOut(auth);
  };

  // Función para cambiar de pestaña guardando la preferencia y reseteando scroll
  const changePage = (newPage) => {
    sessionStorage.setItem('currentPage', newPage);
    setPage(newPage);
    setSelectedItem(null);
    window.scrollTo(0, 0);
  };

  // Función para abrir un detalle guardando la posición actual
  const handleSelectItem = (item) => {
    sessionStorage.setItem('lastScrollPos', window.scrollY.toString());
    setSelectedItem(item);
    window.scrollTo(0, 0);
  };

  const renderContent = () => {
    if (selectedItem) {
      if (selectedItem.status === 'nueva' || selectedItem.status === 'En revisión') {
        if (selectedItem.request_type === 'emprendimiento') {
          return <EmprendimientoDetailsPage request={selectedItem} onBack={() => setSelectedItem(null)} user={user} />;
        } else {
          return <DetailsPage request={selectedItem} onBack={() => setSelectedItem(null)} user={user} />;
        }
      }
      return <UnifiedDetailsView item={selectedItem} onBack={() => setSelectedItem(null)} user={user} />;
    }
    
    switch (page) {
      case 'new':
        return <RequestsListPage user={user} status="nueva" title="Cotizaciones Nuevas" onSelectRequest={handleSelectItem} />;
      case 'orders':
        return <OrdersPage user={user} onSelectOrder={handleSelectItem} />;
      case 'pending':
        return <PendingQuotesPage user={user} />;
      case 'sales':
        return <RequestsListPage user={user} status="completed" title="Mis Ventas" onSelectRequest={handleSelectItem} />;
      case 'finances':
        return <FinancePage user={user} />;
      case 'profile':
        return <BusinessProfilePage user={user} />;
      default:
        return <RequestsListPage user={user} status="nueva" title="Cotizaciones Nuevas" onSelectRequest={handleSelectItem} />;
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="header-logo">
          <img src="/Logo2.png" alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '8px', marginRight: '12px', objectFit: 'cover' }} />
          Repuestos LinkVe
        </div>
        <div className="header-user-info">
          <button className={`nav-button ${page === 'new' ? 'active' : ''}`} onClick={() => changePage('new')}>Cotizaciones Nuevas</button>
          <button className={`nav-button ${page === 'orders' ? 'active' : ''}`} onClick={() => changePage('orders')}>Órdenes</button>
          <button className={`nav-button ${page === 'pending' ? 'active' : ''}`} onClick={() => changePage('pending')}>Cotizaciones Pendientes</button>
          <button className={`nav-button ${page === 'sales' ? 'active' : ''}`} onClick={() => changePage('sales')}>Mis Ventas</button>
          <button className={`nav-button ${page === 'finances' ? 'active' : ''}`} onClick={() => changePage('finances')}>Finanzas</button>
          <button className={`nav-button ${page === 'profile' ? 'active' : ''}`} onClick={() => changePage('profile')}>Mi Perfil</button>
          <span>{user.email}</span>
          <button onClick={handleLogout} style={{backgroundColor: '#6c757d'}}>Cerrar Sesión</button>
        </div>
      </header>
      <main className="dashboard-content">
        {renderContent()}
      </main>
    </div>
  );
};

const RequestsListPage = ({ user, status, title, onSelectRequest }) => {
  const [newRequests, setNewRequests] = useState([]);
  const [inReviewRequests, setInReviewRequests] = useState([]);
  const [submittedQuoteRequestIds, setSubmittedQuoteRequestIds] = useState(new Set());
  const [hiddenRequestIds, setHiddenRequestIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      setNewRequests([]);
      setInReviewRequests([]);
      setSubmittedQuoteRequestIds(new Set());
      setHiddenRequestIds(new Set());
      return;
    }

    const quotesQuery = query(collection(db, "quotes"), where("businessId", "==", user.uid));
    const unsubQuotes = onSnapshot(quotesQuery, (snapshot) => {
      const ids = new Set(snapshot.docs.map(doc => doc.data().requestId));
      setSubmittedQuoteRequestIds(ids);
    });

    if (status !== 'nueva') {
      setLoading(true);
      const q = query(collection(db, "requests"), where("status", "==", status), where("seller_id", "==", user.uid), orderBy("createdAt", "desc"));
      const unsubRequests = onSnapshot(q, (snapshot) => {
        const requestsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setNewRequests(requestsData);
        setInReviewRequests([]);
        setLoading(false);
      }, (error) => {
        console.error("Error al obtener las solicitudes:", error);
        setLoading(false);
      });
      return () => {
        unsubQuotes();
        unsubRequests();
      };
    }
    
    setLoading(true);

    const getProfileAndListen = async () => {
      let businessProfile;
      try {
        const businessDocRef = doc(db, "businesses", user.uid);
        const businessDocSnap = await getDoc(businessDocRef);
        if (businessDocSnap.exists()) {
          businessProfile = businessDocSnap.data();
          setHiddenRequestIds(new Set(businessProfile.hiddenRequests || []));
        } else {
          setLoading(false);
          return [];
        }
      } catch (error) {
        console.error("Error al obtener el perfil de la empresa:", error);
        setLoading(false);
        return [];
      }

      if (!businessProfile?.specialtyVehicleTypes?.length) {
        setNewRequests([]);
        setInReviewRequests([]);
        setLoading(false);
        return [];
      }
      
      const specialties = {
        salesTypes: businessProfile.businessSalesTypes || [],
        brands: businessProfile.specialtyBrands || [],
        vehicleTypes: businessProfile.specialtyVehicleTypes || []
      };

      const qNueva = query(
        collection(db, "requests"),
        where("status", "==", "nueva"),
        where("vehicle_type", "in", specialties.vehicleTypes)
      );
      
      const qEnRevision = query(
        collection(db, "requests"),
        where("status", "==", "En revisión"),
        where("seller_id", "==", user.uid)
      );

      const unsubNueva = onSnapshot(qNueva, (snapshotNuevas) => {
        let nuevasReqs = snapshotNuevas.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        nuevasReqs = nuevasReqs.filter(request => {
            const salesTypeMatch = specialties.salesTypes.includes(request.request_type);
            const brandMatch = specialties.brands.includes(request.brand);
            if (request.request_type === 'emprendimiento') return salesTypeMatch;
            return salesTypeMatch && brandMatch;
        });
        setNewRequests(nuevasReqs);
        setLoading(false);
      });

      const unsubEnRevision = onSnapshot(qEnRevision, (snapshotEnRevision) => {
        const enRevisionReqs = snapshotEnRevision.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInReviewRequests(enRevisionReqs);
        setLoading(false);
      });
      
      return [unsubNueva, unsubEnRevision];
    };

    let cleanupFunctions = [unsubQuotes];
    getProfileAndListen().then(cleanup => {
      if (cleanup) cleanupFunctions.push(...cleanup);
    });
    
    return () => cleanupFunctions.forEach(unsub => unsub && unsub());

  }, [user, status]);

  const filteredRequests = React.useMemo(() => {
    if (status === 'nueva') {
      const combined = [...newRequests, ...inReviewRequests];
      const filtered = combined.filter(req => 
        !submittedQuoteRequestIds.has(req.id) && !hiddenRequestIds.has(req.id)
      );
      return filtered.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    }
    return newRequests.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
  }, [status, newRequests, inReviewRequests, submittedQuoteRequestIds, hiddenRequestIds]);

  const handleHideRequest = async (e, requestId) => {
    e.stopPropagation();
    if (!window.confirm("¿Deseas eliminar esta solicitud de tu panel?")) return;
    try {
      const businessRef = doc(db, "businesses", user.uid);
      await updateDoc(businessRef, { hiddenRequests: arrayUnion(requestId) });
      setHiddenRequestIds(prev => new Set([...prev, requestId]));
    } catch (error) { console.error("Error:", error); }
  };

  const handleRequestClick = (request) => {
    sessionStorage.setItem('lastScrollPos', window.scrollY.toString());
    onSelectRequest(request);
  };
  
  const cardStyle = { backgroundColor: 'white', border: '1px solid #e9ecef', borderRadius: '8px', padding: '20px 24px', marginBottom: '16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', transition: 'all 0.2s ease-in-out' };
  const cardInfoStyle = { display: 'flex', flexDirection: 'column', gap: '4px' };
  const cardInfoH3Style = { margin: 0, fontSize: '1.1rem', color: '#005A9C' };
  const cardInfoPStyle = { margin: 0, color: '#6c757d', fontSize: '0.9rem' };
  const cardMetaStyle = { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', textAlign: 'right', flexShrink: 0, paddingLeft: '16px' };
  
  const getStatusText = (status) => {
    switch(status) {
      case 'nueva': return 'Nueva';
      case 'En revisión': return 'En Revisión';
      case 'completed': return 'Completado';
      default: return status;
    }
  };
  const getStatusClassName = (status) => !status ? '' : `status-${status.toLowerCase().replace(/ /g, '-').replace(/_/g, '-')}`;
  
  const getRequestTitle = (request) => {
    const type = request.request_type;
    const partsCount = request.parts_list ? request.parts_list.length : 0;
    const repuestoText = partsCount === 1 ? '1 Repuesto' : 'Varios Repuestos';
    switch (type) {
      case 'detal': return `Solicitud Al Detal (${repuestoText})`;
      case 'wholesale': return `Solicitud Al Mayor (${repuestoText})`;
      case 'chivera': return `Solicitudes a Chiveras (${repuestoText})`;
      case 'emprendimiento': return 'Solicitud para Emprendimiento';
      default: return `Solicitud (${partsCount} Repuestos)`;
    }
  };

  return (
    <>
      <h2>{title}</h2>
      {loading ? (
        <p>Cargando solicitudes...</p>
      ) : filteredRequests.length > 0 ? (
        filteredRequests.map(request => (
          <div key={request.id} style={cardStyle} onClick={() => handleRequestClick(request)}>
            <div style={cardInfoStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <h3 style={cardInfoH3Style}>{getRequestTitle(request)}</h3>
              </div>

              {request.request_type === 'emprendimiento' ? (
                <p style={cardInfoPStyle}>Área de Negocio: {request.business_area}</p>
              ) : (
                <p style={cardInfoPStyle}>
                  Vehículo: {request.vehicle_details || `${request.brand || ''} ${request.model || ''} ${request.year || ''}`}
                  
                  {status !== 'nueva' && (request.part_name || request.partName || (request.parts_list && request.parts_list[0]?.part_name)) && (
                    <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#495057' }}>
                      - {request.part_name || request.partName || request.parts_list[0].part_name}
                    </span>
                  )}
                </p>
              )}
            </div>

            <div style={cardMetaStyle}>
              <span className={`status-badge ${getStatusClassName(request.status)}`}>{getStatusText(request.status)}</span>
              
              {request.readableId && (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#6c757d', marginTop: '4px' }}>
                  ID: {request.readableId}
                </span>
              )}

              <p style={{ ...cardInfoPStyle, marginTop: '2px' }}>
                {new Date(request.createdAt?.seconds * 1000).toLocaleDateString('es-VE')}
              </p>
              
              {status === 'nueva' && (
                <button 
                  onClick={(e) => handleHideRequest(e, request.id)}
                  style={{ marginTop: '8px', backgroundColor: '#fff1f1', border: '1px solid #ffc1c1', color: '#dc3545', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🗑️ Quitar de mi panel
                </button>
              )}
            </div>
          </div>
        ))
      ) : (
        <p>No hay solicitudes en esta sección.</p>
      )}
    </>
  );
};

const BusinessChatWidget = ({ quoteId, user }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    useEffect(() => {
        const q = query(
            collection(db, "quotes", quoteId, "messages"),
            orderBy("timestamp", "asc")
        );
        const unsub = onSnapshot(q, (snapshot) => {
            setMessages(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const markAsRead = async () => {
            try {
                await updateDoc(doc(db, "quotes", quoteId), {
                    hasUnreadMessageForBusiness: false
                });
            } catch (error) {
                console.error("Error quitando notificación:", error);
            }
        };
        markAsRead();

        return () => unsub();
    }, [quoteId]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const text = newMessage.trim();
        if (!text) return;

        setIsSending(true);
        try {
            await addDoc(collection(db, "quotes", quoteId, "messages"), {
                text: text,
                senderId: user.uid,
                senderRole: 'business',
                timestamp: serverTimestamp()
            });
            
            await updateDoc(doc(db, "quotes", quoteId), {
                hasUnreadMessageForUser: true
            });

            setNewMessage('');
        } catch (error) {
            console.error("Error al enviar mensaje:", error);
            alert("Hubo un error al enviar el mensaje.");
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="chat-widget" style={{ border: '1px solid #ddd', borderRadius: '8px', display: 'flex', flexDirection: 'column', height: '400px', backgroundColor: '#fdfdfd' }}>
            <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd', borderRadius: '8px 8px 0 0' }}>
                <h4 style={{ margin: 0, color: '#005A9C' }}>💬 Aclaratorias con el Cliente</h4>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {messages.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#aaa', fontSize: '14px', marginTop: 'auto', marginBottom: 'auto' }}>No hay mensajes aún. Escribe al cliente si tienes dudas.</p>
                ) : (
                    messages.map(msg => {
                        const isMe = msg.senderRole === 'business';
                        return (
                            <div key={msg.id} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                                <div style={{
                                    backgroundColor: isMe ? '#005A9C' : '#e9ecef',
                                    color: isMe ? 'white' : 'black',
                                    padding: '10px 14px',
                                    borderRadius: '15px',
                                    borderBottomRightRadius: isMe ? '0' : '15px',
                                    borderBottomLeftRadius: !isMe ? '0' : '15px',
                                }}>
                                    {msg.text}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <form onSubmit={handleSendMessage} style={{ display: 'flex', padding: '10px', borderTop: '1px solid #ddd', backgroundColor: 'white', borderRadius: '0 0 8px 8px' }}>
                <input 
                    type="text" 
                    value={newMessage} 
                    onChange={(e) => setNewMessage(e.target.value)} 
                    placeholder="Escribe tu mensaje..." 
                    style={{ flex: 1, padding: '10px', border: '1px solid #ccc', borderRadius: '20px', marginRight: '10px' }}
                    disabled={isSending}
                />
                <button type="submit" disabled={isSending} style={{ padding: '10px 20px', borderRadius: '20px', backgroundColor: '#005A9C', color: 'white', border: 'none', cursor: 'pointer' }}>
                    {isSending ? '...' : 'Enviar'}
                </button>
            </form>
        </div>
    );
    
};

const EditPendingQuotePage = ({ quote, onBack, user }) => {
    const [quotedParts, setQuotedParts] = useState(quote.quoted_parts || []);
    const [isUpdating, setIsUpdating] = useState(false);
    const [quoteMessage, setQuoteMessage] = useState('');

    const [deliveryType, setDeliveryType] = useState(quote.deliveryType || 'No Ofrece');
    const [deliveryCost, setDeliveryCost] = useState(quote.deliveryCost || '');
    const [deliveryCostBs, setDeliveryCostBs] = useState(quote.deliveryCostBs || '');

    const initialImages = {};
    (quote.quoted_parts || []).forEach((part, index) => {
        if (part.imageUrls && part.imageUrls.length > 0) {
            initialImages[index] = part.imageUrls;
        }
    });
    const [partImages, setPartImages] = useState(initialImages);
    const [uploading, setUploading] = useState({});

    const removePartFromEdit = (index) => {
        if (quotedParts.length === 1) {
            alert("No puedes dejar la cotización vacía. Si no tienes este repuesto, utiliza el botón 'Retirar Cotización' arriba a la derecha.");
            return;
        }

        if (window.confirm(`¿Seguro que quieres eliminar "${quotedParts[index].part_name}"? El cliente ya no verá este repuesto al actualizar.`)) {
            const updatedParts = quotedParts.filter((_, i) => i !== index);
            setQuotedParts(updatedParts);

            const updatedImages = { ...partImages };
            delete updatedImages[index];
            setPartImages(updatedImages);
        }
    };

    const handlePartChange = (index, field, value) => {
        const updatedParts = [...quotedParts];
        updatedParts[index][field] = value;
        setQuotedParts(updatedParts);
    };

    const handleImageUpload = async (event, index) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(prev => ({ ...prev, [index]: true }));
        setQuoteMessage('Subiendo imágenes...');
        
        const part = quotedParts[index];
        const uploadPromises = files.map(file => {
            const filePath = `quotes/${quote.requestId}/${part.part_name.replace(/\s+/g, '_')}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, filePath);
            return uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef));
        });

        try {
            const downloadURLs = await Promise.all(uploadPromises);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: [...(prevImages[index] || []), ...downloadURLs]
            }));
        } catch (error) {
            console.error("Error al subir imagen: ", error);
            setQuoteMessage("Error al subir una de las imágenes.");
        } finally {
            setUploading(prev => ({ ...prev, [index]: false }));
            setQuoteMessage('');
        }
    };

    const handleRemoveImage = async (index, imageUrl) => {
        if (!window.confirm("¿Seguro que quieres eliminar esta foto?")) return;
        try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: prevImages[index].filter(url => url !== imageUrl)
            }));
        } catch (error) {
            console.error("Error al eliminar imagen: ", error);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: prevImages[index].filter(url => url !== imageUrl)
            }));
        }
    };

    const handleUpdateQuote = async (e) => {
        e.preventDefault();

        if (Object.values(uploading).some(status => status === true)) {
            setQuoteMessage("Espera a que terminen de subirse las imágenes.");
            return;
        }

        setIsUpdating(true);
        setQuoteMessage('');

        try {
            const partsToUpdate = quotedParts.map((p, index) => ({
                ...p,
                price: parseFloat(p.price) || 0,
                price_bs: parseFloat(p.price_bs) || 0,
                quantity: parseInt(p.quantity, 10) || 1,
                imageUrls: partImages[index] || []
            }));

            const newTotalPrice = partsToUpdate.reduce((sum, part) => sum + part.price, 0);
            const newTotalPriceBs = partsToUpdate.reduce((sum, part) => sum + part.price_bs, 0);

            const quoteRef = doc(db, "quotes", quote.id);
            await updateDoc(quoteRef, {
                quoted_parts: partsToUpdate,
                totalPrice: newTotalPrice,
                totalPriceBs: newTotalPriceBs,
                deliveryType: deliveryType,
                deliveryCost: deliveryType === 'Pago' ? parseFloat(deliveryCost || 0) : 0,
                deliveryCostBs: deliveryType === 'Pago' ? parseFloat(deliveryCostBs || 0) : 0,
                part_name: partsToUpdate.length === 1 ? partsToUpdate[0].part_name : `Cotización (${partsToUpdate.length} Repuestos)`,
                updatedAt: serverTimestamp()
            });

            alert('¡Cotización actualizada exitosamente!');
        } catch (error) {
            console.error("Error actualizando:", error);
            setQuoteMessage("Error al actualizar la cotización.");
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteQuote = async () => {
        if (!window.confirm("¿Estás seguro de que deseas retirar esta cotización?")) return;
        try {
            await deleteDoc(doc(db, "quotes", quote.id));
            alert("Cotización retirada correctamente.");
            onBack();
        } catch (error) {
            console.error("Error eliminando:", error);
            alert("Error al retirar la cotización.");
        }
    };

    return (
        <main className="details-view">
            <button onClick={onBack} className="back-button">&larr; Volver a Pendientes</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h2>Gestionar Cotización</h2>
                <button onClick={handleDeleteQuote} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer' }}>
                    🗑️ Retirar Cotización
                </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
                <div>
                    <div className="details-card" style={{ marginBottom: '20px' }}>
                        <p><strong>Vehículo Solicitado:</strong> {quote.vehicle_details}</p>
                        <p><strong>Estado Actual:</strong> <span className="status-badge status-en-revisión">Esperando al cliente</span></p>
                    </div>

                    <form onSubmit={handleUpdateQuote}>
                        <h3>Modificar Detalles de la Oferta</h3>
                        {quotedParts.map((part, index) => (
                            <div key={index} className="quote-form-item" style={{ position: 'relative', padding: '15px', border: '1px solid #eee', borderRadius: '8px', marginBottom: '15px', backgroundColor: '#fff' }}>
                                
                                <button 
                                    type="button" 
                                    onClick={() => removePartFromEdit(index)}
                                    style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: '#fff', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '5px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                                >
                                    ❌ Quitar
                                </button>

                                <h4>{part.part_name} (Cant: {part.quantity})</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Precio Total ($)</label>
                                        <input type="number" step="0.01" value={part.price} onChange={(e) => handlePartChange(index, 'price', e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Precio Total (Bs.)</label>
                                        <input type="number" step="0.01" value={part.price_bs} onChange={(e) => handlePartChange(index, 'price_bs', e.target.value)} required />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Marca</label>
                                        <input type="text" value={part.partBrand} onChange={(e) => handlePartChange(index, 'partBrand', e.target.value)} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Calidad</label>
                                        <select value={part.partType || 'Original'} onChange={(e) => handlePartChange(index, 'partType', e.target.value)}>
                                            <option value="Original">Original</option>
                                            <option value="Genérico Alta Calidad">Genérico Alta Calidad</option>
                                            <option value="Genérico Media Calidad">Genérico Media Calidad</option>
                                            <option value="Genérico Baja Calidad">Genérico Baja Calidad</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div className="image-upload-section" style={{ marginTop: '15px' }}>
                                    <label className="image-upload-label">Fotos del Repuesto</label>
                                    <input type="file" multiple accept="image/*" id={`edit-file-input-${index}`} style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, index)} disabled={uploading[index]} />
                                    <button type="button" className="upload-btn" onClick={() => document.getElementById(`edit-file-input-${index}`).click()} disabled={uploading[index]}>
                                        {uploading[index] ? 'Subiendo...' : '[+] Modificar / Añadir Fotos'}
                                    </button>
                                    <div className="thumbnails-container">
                                        {(partImages[index] || []).map(imageUrl => (
                                            <div key={imageUrl} className="thumbnail-item">
                                                <img src={imageUrl} alt="Repuesto" />
                                                <button type="button" className="remove-btn" onClick={() => handleRemoveImage(index, imageUrl)}>&times;</button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="quote-form-item" style={{ padding: '15px', border: '1px solid #eee', borderRadius: '8px', marginBottom: '15px' }}>
                            <h4>Opciones de Delivery</h4>
                            <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>
                                <option value="No Ofrece">No Ofrece</option>
                                <option value="Gratis">Gratis</option>
                                <option value="Pago">Pago</option>
                            </select>
                            {deliveryType === 'Pago' && (
                                <div className="form-row" style={{ marginTop: '10px' }}>
                                    <input type="number" step="0.01" value={deliveryCost} onChange={(e) => setDeliveryCost(e.target.value)} placeholder="Costo $" />
                                    <input type="number" step="0.01" value={deliveryCostBs} onChange={(e) => setDeliveryCostBs(e.target.value)} placeholder="Costo Bs." />
                                </div>
                            )}
                        </div>

                        <button type="submit" disabled={isUpdating || Object.values(uploading).some(status => status === true)} style={{ width: '100%', padding: '15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                            {isUpdating ? 'Actualizando...' : 'ACTUALIZAR COTIZACIÓN'}
                        </button>
                        {quoteMessage && <p style={{ marginTop: '10px', textAlign: 'center' }}>{quoteMessage}</p>}
                    </form>
                </div>

                <div>
                    <BusinessChatWidget quoteId={quote.id} user={user} />
                </div>
            </div>
        </main>
    );
};

const PendingQuotesPage = ({ user }) => {
    const [pendingQuotes, setPendingQuotes] = useState([]);
    const [rejectedQuotes, setRejectedQuotes] = useState([]);
    const [selectedQuote, setSelectedQuote] = useState(null);

    useEffect(() => {
        const pendingQuery = query(collection(db, "quotes"), where("businessId", "==", user.uid), where("quoteStatus", "==", "Enviada"), orderBy("createdAt", "desc"));
        const pendingUnsub = onSnapshot(pendingQuery, (snapshot) => {
            setPendingQuotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        const rejectedQuery = query(collection(db, "quotes"), where("businessId", "==", user.uid), where("quoteStatus", "==", "Rechazada"), orderBy("createdAt", "desc"));
        const rejectedUnsub = onSnapshot(rejectedQuery, (snapshot) => {
            setRejectedQuotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            pendingUnsub();
            rejectedUnsub();
        };
    }, [user.uid]);

    const handleDeleteQuote = async (quoteId) => {
        if (!window.confirm("¿Deseas eliminar esta cotización rechazada?")) return;
        try {
            await deleteDoc(doc(db, "quotes", quoteId));
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Fecha no disponible';
        return new Date(timestamp.seconds * 1000).toLocaleDateString('es-VE');
    }

    if (selectedQuote) {
        return <EditPendingQuotePage quote={selectedQuote} onBack={() => setSelectedQuote(null)} user={user} />;
    }

    return (
        <>
            <h2>Cotizaciones Pendientes</h2>
            <h3 className="section-title">Cotizaciones en Revisión (Haz clic para ver chat o editar)</h3>
            {pendingQuotes.length > 0 ? (
                pendingQuotes.map(quote => {
                    const hasUnread = quote.hasUnreadMessageForBusiness === true;

                    return (
                        <div key={quote.id} className="request-card" style={{
                            cursor: 'pointer', 
                            transition: 'all 0.2s', 
                            borderLeft: hasUnread ? '6px solid #dc3545' : '4px solid #005A9C',
                            backgroundColor: hasUnread ? '#fff5f5' : 'white',
                            boxShadow: hasUnread ? '0 4px 8px rgba(220, 53, 69, 0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '20px',
                            marginBottom: '15px',
                            borderRadius: '8px'
                        }} onClick={() => {
                            sessionStorage.setItem('lastScrollPos', window.scrollY.toString());
                            setSelectedQuote(quote);
                        }}>
                            <div className="request-card-info">
                                <h3 style={{ margin: 0, color: '#005A9C', fontSize: '1.1rem' }}>
                                    {
                                        (quote.request_type === 'wholesale' || (quote.quoted_parts && quote.quoted_parts.length > 1))
                                            ? `Cotización (${quote.quoted_parts?.length || 0} Repuestos)`
                                            : (quote.part_name || quote.partName || "Cotización")
                                    }
                                </h3>
                                
                                <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                                    Vehículo: {quote.vehicle_details || 'No especificado'}
                                    
                                    {(quote.part_name || (quote.quoted_parts && quote.quoted_parts[0]?.part_name)) && (
                                        <span style={{ marginLeft: '8px', fontWeight: 'bold', color: '#495057' }}>
                                            - {quote.part_name || quote.quoted_parts[0].part_name}
                                        </span>
                                    )}
                                </p>
                                
                                <div style={{ marginTop: '12px' }}>
                                    {hasUnread ? (
                                        <span style={{ backgroundColor: '#dc3545', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' }}>
                                            💬 ¡Tienes un mensaje nuevo!
                                        </span>
                                    ) : (
                                        <p style={{ color: '#005A9C', fontSize: '13px', margin: 0, fontWeight: 'bold' }}>
                                            💬 Abrir chat o modificar oferta &rarr;
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="request-card-meta" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                <span className="status-badge status-en-revisión">Enviada</span>
                                
                                {quote.readableId && (
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#6c757d', marginTop: '4px' }}>
                                        ID: {quote.readableId}
                                    </span>
                                )}
                                
                                <p style={{ margin: 0, color: '#6c757d', fontSize: '0.9rem' }}>{formatDate(quote.createdAt)}</p>
                            </div>
                        </div>
                    );
                })
            ) : (
                <p>No tienes cotizaciones esperando respuesta del cliente.</p>
            )}

            <h3 className="section-title">Cotizaciones Rechazadas</h3>
            {rejectedQuotes.length > 0 ? (
                rejectedQuotes.map(quote => (
                    <div key={quote.id} className="quote-card" style={{ cursor: 'default' }}>
                        <div className="quote-card-info">
                            <h3>{quote.part_name || quote.partName}</h3>
                            <p>Precio Ofertado: <strong>${(quote.totalPrice || quote.price || 0).toFixed(2)}</strong></p>
                        </div>
                        <div className="quote-card-meta">
                            <span className="status-badge quote-status-rechazada">{quote.quoteStatus}</span>
                            <button onClick={() => handleDeleteQuote(quote.id)} className="delete-button" title="Eliminar">🗑️</button>
                        </div>
                    </div>
                ))
            ) : (
                <p>No tienes cotizaciones rechazadas.</p>
            )}
        </>
    );
};

// Función auxiliar para renderizar los métodos de pago en el perfil de manera segura
const renderPaymentInfo = (paymentObj) => {
    if (!paymentObj) return 'No definido';
    if (typeof paymentObj === 'string') return paymentObj; // Retrocompatibilidad para los viejos datos
    
    // Si es el nuevo objeto, lo mapeamos para que se lea de forma limpia en el panel de empresa
    const parts = [];
    if (paymentObj.banco) parts.push(`Banco: ${paymentObj.banco}`);
    if (paymentObj.cedula) parts.push(`Cédula/RIF: ${paymentObj.cedula}`);
    if (paymentObj.telefono) parts.push(`Telf: ${paymentObj.telefono}`);
    if (paymentObj.cuenta) parts.push(`Cuenta: ${paymentObj.cuenta}`);
    if (paymentObj.titular) parts.push(`Titular: ${paymentObj.titular}`);
    if (paymentObj.rifCedula) parts.push(`Cédula/RIF: ${paymentObj.rifCedula}`);
    if (paymentObj.correo) parts.push(`Correo: ${paymentObj.correo}`);
    if (paymentObj.detalles) parts.push(`Detalles: ${paymentObj.detalles}`);
    if (paymentObj.payId) parts.push(`Pay ID: ${paymentObj.payId}`);
    if (paymentObj.email) parts.push(`Email: ${paymentObj.email}`);
    if (paymentObj.usuario) parts.push(`Usuario: ${paymentObj.usuario}`);
    
    return parts.length > 0 ? parts.join(' | ') : 'No definido';
};

const BusinessProfilePage = ({ user }) => {
    const [profileData, setProfileData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            setLoading(true);
            const docRef = doc(db, "businesses", user.uid);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setProfileData(docSnap.data());
            } else {
                console.error("No se encontró el perfil para la empresa con UID:", user.uid);
                setProfileData({});
            }
            setLoading(false);
        };
        fetchProfile();
    }, [user.uid]);
    
    if (loading) {
        return <p>Cargando perfil...</p>;
    }

    return (
        <div className="profile-container">
            <div className="profile-header">
                <h2>Mi Perfil de Empresa</h2>
            </div>
            <div className="profile-view">
                <div className="profile-section">
                    <h3>Información de Contacto y Ubicación</h3>
                    <div className="profile-data-row"><span className="data-label">Nombre:</span><span>{profileData?.business_name || 'No definido'}</span></div>
                    <div className="profile-data-row"><span className="data-label">ID de Vendedor:</span><span>{profileData?.seller_id || 'No definido'}</span></div>
                    <div className="profile-data-row"><span className="data-label">Teléfono:</span><span>{profileData?.business_phone || 'No definido'}</span></div>
                    <div className="profile-data-row"><span className="data-label">Ubicación Aprox.:</span><span>{profileData?.business_approximate_location || 'No definida'}</span></div>
                    <div className="profile-data-row"><span className="data-label">Dirección Exacta:</span><span>{profileData?.business_exact_address || 'No definida'}</span></div>
                </div>
                <div className="profile-section">
                    <h3>Métodos de Pago</h3>
                    <div className="profile-data-row"><span className="data-label">Pago Móvil:</span><span>{renderPaymentInfo(profileData?.pago_movil)}</span></div>
                    <div className="profile-data-row"><span className="data-label">Binance:</span><span>{renderPaymentInfo(profileData?.binance)}</span></div>
                    <div className="profile-data-row"><span className="data-label">Zelle:</span><span>{renderPaymentInfo(profileData?.zelle)}</span></div>
                    <div className="profile-data-row"><span className="data-label">Banco Internacional:</span><span>{renderPaymentInfo(profileData?.international_bank)}</span></div>
                    <div className="profile-data-row"><span className="data-label">Transferencia (Nacional):</span><span>{renderPaymentInfo(profileData?.transferencia_bs)}</span></div>
                    <div className="profile-data-row"><span className="data-label">Efectivo:</span><span>{profileData?.efectivo || 'No definido'}</span></div>
                </div>
            </div>
        </div>
    );
};

const OrdersPage = ({ user, onSelectOrder }) => {
    const [orders, setOrders] = useState([]);
    
    useEffect(() => {
    const q = query(
      collection(db, "requests"), 
      where("seller_id", "==", user.uid), 
      where("status", "in", ["payment_verifying", "preparing_shipment", "ready_for_pickup", "shipped", "payment_rejected"]),
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const ordersData = snapshot.docs.map(doc => {
            const data = doc.data();
            const userData = {
                fullName: data.shipping_receiver_name || "Cliente (verificar envío)",
                email: data.userEmail || "No disponible"
            };
            return { id: doc.id, ...data, userData };
        });
        
        ordersData.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));

        setOrders(ordersData);
    }, (error) => {
        console.error("Error en la consulta de órdenes:", error);
    });
    
    return () => unsubscribe();
  }, [user]);
    
    const getStatusText = (status) => {
        switch(status) {
            case 'payment_verifying': return 'Verificando Pago';
            case 'preparing_shipment': return 'Preparando Envío';
            case 'ready_for_pickup': return 'Listo para Retirar';
            case 'shipped': return 'Enviado';
            case 'completed': return 'Completado';
            case 'payment_rejected': return 'Pago Rechazado';
            default: return status;
        }
    };

    const getStatusClassName = (status) => {
        if (!status) return '';
        return `status-${status.toLowerCase().replace(/_/g, '-')}`;
    }

    return (
        <div className="orders-container">
            <h2>Gestión de Órdenes</h2>
            <div className="orders-column">
                <h3 className="section-title">Órdenes Activas ({orders.length})</h3>
                {orders.length > 0 ? (
                    orders.map(order => (
                        <div key={order.id} className={`order-card status-border-${order.status?.replace(/_/g, '-')}`} onClick={() => {
                            sessionStorage.setItem('lastScrollPos', window.scrollY.toString());
                            onSelectOrder(order);
                        }}>
                            <div className="order-card-summary">
                                <div>
                                    <p><strong>Cliente:</strong> {order.userData.fullName}</p>
                                    <p><strong>Estado:</strong> <span className={`status-badge ${getStatusClassName(order.status)}`}>{getStatusText(order.status)}</span></p>
                                </div>
                                <button>Ver Detalles</button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No hay órdenes activas en este momento.</p>
                )}
            </div>
        </div>
    );
};

const EmprendimientoDetailsPage = ({ request, onBack, user }) => {
    const [proposedParts, setProposedParts] = useState([
        { part_name: '', quantity: 1, price: '', price_bs: '', partBrand: '', partType: 'Original' }
    ]);
    const [partImages, setPartImages] = useState({});
    const [uploading, setUploading] = useState({});
    const [generalNotes, setGeneralNotes] = useState('');
    const [deliveryType, setDeliveryType] = useState('No Ofrece');
    const [deliveryCost, setDeliveryCost] = useState('');
    const [deliveryCostBs, setDeliveryCostBs] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [quoteMessage, setQuoteMessage] = useState('');

    const handlePartChange = (index, field, value) => {
        const updatedParts = [...proposedParts];
        updatedParts[index][field] = value;
        setProposedParts(updatedParts);
    };

    const addPart = () => {
        setProposedParts([...proposedParts, { part_name: '', quantity: 1, price: '', price_bs: '', partBrand: '', partType: 'Original' }]);
    };

    const removePart = (index) => {
        const updatedParts = proposedParts.filter((_, i) => i !== index);
        setProposedParts(updatedParts);
        const updatedImages = { ...partImages };
        delete updatedImages[index];
        setPartImages(updatedImages);
    };
    
    const handleImageUpload = async (event, index) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(prev => ({ ...prev, [index]: true }));
        setQuoteMessage('Subiendo imágenes...');
        
        const part = proposedParts[index];
        const partNameForPath = part.part_name || `repuesto_${index + 1}`;
        
        const uploadPromises = files.map(file => {
            const filePath = `quotes/${request.id}/${partNameForPath.replace(/\s+/g, '_')}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, filePath);
            return uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef));
        });

        try {
            const downloadURLs = await Promise.all(uploadPromises);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: [...(prevImages[index] || []), ...downloadURLs]
            }));
        } catch (error) {
            console.error("Error al subir imagen: ", error);
            setQuoteMessage("Error al subir una de las imágenes.");
        } finally {
            setUploading(prev => ({ ...prev, [index]: false }));
            setQuoteMessage('');
        }
    };

    const handleRemoveImage = async (index, imageUrl) => {
        if (!window.confirm("¿Seguro que quieres eliminar esta imagen?")) return;
        try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: prevImages[index].filter(url => url !== imageUrl)
            }));
        } catch (error) {
            console.error("Error al eliminar imagen: ", error);
            setQuoteMessage("Error al eliminar la imagen.");
        }
    };

    const handleSendProposal = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setQuoteMessage('');

        const businessProfileRef = doc(db, "businesses", user.uid);
        const businessProfileSnap = await getDoc(businessProfileRef);

        if (!businessProfileSnap.exists()) {
            setQuoteMessage('Error: No se pudo encontrar el perfil de tu empresa.');
            setIsLoading(false);
            return;
        }

        try {
            const businessProfileData = businessProfileSnap.data();

            const businessDataForQuote = {
                businessSalesTypes: businessProfileData.businessSalesTypes || [],
                business_approximate_location: businessProfileData.business_approximate_location || '',
                business_exact_address: businessProfileData.business_exact_address || '',
                business_name: businessProfileData.business_name || '',
                business_phone: businessProfileData.business_phone || '',
                email: businessProfileData.email || '',
                rif: businessProfileData.rif || '',

                pago_movil: businessProfileData.pago_movil || '',
                binance: businessProfileData.binance || null, // <- BINANCE AÑADIDO AL PAYLOAD
                zelle: businessProfileData.zelle || '',
                international_bank: businessProfileData.international_bank || '',
                transferencia_bs: businessProfileData.transferencia_bs || '',
                efectivo: businessProfileData.efectivo || ''
            };

            const partsToQuote = proposedParts.map((p, index) => ({
                ...p,
                price: parseFloat(p.price) || 0,
                price_bs: parseFloat(p.price_bs) || 0,
                quantity: parseInt(p.quantity, 10) || 1,
                imageUrls: partImages[index] || []
            }));

            const totalPrice = partsToQuote.reduce((sum, part) => sum + (part.price * part.quantity), 0);
            const totalPriceBs = partsToQuote.reduce((sum, part) => sum + (part.price_bs * part.quantity), 0);

            const quoteData = {
                readableId: request.readableId,
                requestId: request.id,
                businessId: user.uid,
                userId: request.userId,
                createdAt: serverTimestamp(),
                quoteStatus: 'Enviada',
                notes: generalNotes,
                deliveryType: deliveryType,
                deliveryCost: deliveryType === 'Pago' ? parseFloat(deliveryCost || 0) : 0,
                deliveryCostBs: deliveryType === 'Pago' ? parseFloat(deliveryCostBs || 0) : 0,
                partName: `Propuesta para Emprendimiento`,
                totalPrice,
                totalPriceBs,
                quoted_parts: partsToQuote,
                request_type: request.request_type,
                vehicle_details: `Presupuesto: $${request.investment_amount}`,
                ...businessDataForQuote
            };

            delete quoteData.businessEmail;

            const customQuoteId = `${request.id}_${user.uid}`;
            
            const quoteRef = doc(db, "quotes", customQuoteId);
            await setDoc(quoteRef, quoteData);

            setQuoteMessage('¡Propuesta enviada con éxito!');
            setTimeout(() => onBack(), 2000);
        } catch (error) {
            console.error("Error al enviar propuesta: ", error);
            setQuoteMessage('Error al enviar la propuesta.');
        } finally {
            setIsLoading(false);
        }
    };

    const totalUSD = proposedParts.reduce((sum, part) => sum + (parseFloat(part.price || 0) * (part.quantity || 1)), 0);
    const totalBs = proposedParts.reduce((sum, part) => sum + (parseFloat(part.price_bs || 0) * (part.quantity || 1)), 0);

    return (
        <main className="details-view">
            <button onClick={onBack} className="back-button">&larr; Volver</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2>Propuesta para Emprendimiento</h2>
                {request.readableId && (
                    <span style={{
                        backgroundColor: '#e9ecef', color: '#6c757d', padding: '5px 10px',
                        borderRadius: '8px', fontWeight: 'bold'
                    }}>
                        ID: {request.readableId}
                    </span>
                )}
            </div>
            
            <div className="details-card">
                <p><strong>Área de Negocio Solicitada:</strong> {request.business_area}</p>
                <p><strong>Presupuesto de Inversión del Cliente:</strong> ${request.investment_amount?.toFixed(2)}</p>
                <p><strong>Zona de Operación (Cliente):</strong> {request.delivery_zone}</p>
                <p><strong>Fecha:</strong> {new Date(request.createdAt?.seconds * 1000).toLocaleDateString('es-VE')}</p>
            </div>

            <form onSubmit={handleSendProposal} style={{marginTop: '20px'}}>
                <h3>Crear Propuesta de Repuestos</h3>
                {proposedParts.map((part, index) => (
                    <div key={index} className="quote-form-item proposed-part">
                        <div className="proposed-part-header">
                            <h4>Repuesto Propuesto #{index + 1}</h4>
                           {proposedParts.length > 1 && (
                               <button type="button" className="remove-part-btn" onClick={() => removePart(index)}>Eliminar</button>
                           )}
                        </div>
                        <div className="form-group">
                            <label>Nombre del Repuesto</label>
                            <input type="text" value={part.part_name} onChange={(e) => handlePartChange(index, 'part_name', e.target.value)} placeholder="Ej: Kit de Bujías" required />
                        </div>
                        <div className="form-row">
                             <div className="form-group">
                                <label>Cantidad</label>
                                <input type="number" value={part.quantity} onChange={(e) => handlePartChange(index, 'quantity', e.target.value)} placeholder="1" min="1" required />
                            </div>
                           <div className="form-group">
                                <label>Precio por Unidad ($)</label>
                                <input type="number" step="0.01" value={part.price} onChange={(e) => handlePartChange(index, 'price', e.target.value)} placeholder="Ej: 25.50" required />
                            </div>
                           <div className="form-group">
                                <label>Precio por Unidad (Bs.)</label>
                                <input type="number" step="0.01" value={part.price_bs} onChange={(e) => handlePartChange(index, 'price_bs', e.target.value)} placeholder="Ej: 920.50" required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Marca del Repuesto</label>
                                <input type="text" value={part.partBrand} onChange={(e) => handlePartChange(index, 'partBrand', e.target.value)} placeholder="Ej: ACDelco" required />
                            </div>
                            <div className="form-group">
                                <label>Calidad del Repuesto</label>
                                <select value={part.partType} onChange={(e) => handlePartChange(index, 'partType', e.target.value)}>
                                    <option value="Original">Original</option>
                                    <option value="Genérico Alta Calidad">Genérico Alta Calidad</option>
                                    <option value="Genérico Media Calidad">Genérico Media Calidad</option>
                                    <option value="Genérico Baja Calidad">Genérico Baja Calidad</option>
                                </select>
                            </div>
                        </div>
                         <div className="image-upload-section">
                            <label className="image-upload-label">Adjuntar Fotos del Repuesto</label>
                            <input type="file" multiple accept="image/*" id={`file-input-${index}`} style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, index)} disabled={uploading[index]}/>
                            <button type="button" className="upload-btn" onClick={() => document.getElementById(`file-input-${index}`).click()} disabled={uploading[index]}>
                                {uploading[index] ? 'Subiendo...' : '[+] Seleccionar Archivos'}
                            </button>
                            <div className="thumbnails-container">
                                {(partImages[index] || []).map(imageUrl => (
                                    <div key={imageUrl} className="thumbnail-item">
                                        <img src={imageUrl} alt="Repuesto" />
                                        <button type="button" className="remove-btn" onClick={() => handleRemoveImage(index, imageUrl)}>&times;</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
                
                <button type="button" className="add-part-btn" onClick={addPart}>+ Añadir otro repuesto a la propuesta</button>
                
                <div className="proposal-summary">
                    <h4>Total de la Propuesta: ${totalUSD.toFixed(2)} / Bs. {totalBs.toFixed(2)}</h4>
                </div>
                
                <div className="quote-form-item">
                    <h4>Opciones de Delivery (para toda la orden)</h4>
                    <div className="form-group">
                        <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>
                            <option value="No Ofrece">No Ofrece</option>
                            <option value="Gratis">Gratis</option>
                            <option value="Pago">Pago</option>
                        </select>
                    </div>
                    {deliveryType === 'Pago' && (
                        <div className="form-row">
                             <div className="form-group">
                                <label>Costo del Delivery ($)</label>
                                <input type="number" step="0.01" value={deliveryCost} onChange={(e) => setDeliveryCost(e.target.value)} placeholder="Ej: 5.00" />
                            </div>
                           <div className="form-group">
                                <label>Costo del Delivery (Bs.)</label>
                                <input type="number" step="0.01" value={deliveryCostBs} onChange={(e) => setDeliveryCostBs(e.target.value)} placeholder="Ej: 185.00" />
                            </div>
                        </div>
                    )}
                </div>
                <div className="form-group">
                    <label>Notas Generales de la Propuesta</label>
                    <textarea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} placeholder="Ej: Propuesta válida por 3 días, incluye asesoría inicial..." />
                </div>

                <button type="submit" disabled={isLoading || Object.values(uploading).some(status => status)}>
                    {isLoading ? 'Enviando...' : 'Enviar Propuesta'}
                </button>
                {quoteMessage && <p style={{ marginTop: '15px', color: quoteMessage.includes('Error') ? '#dc3545' : '#28a745' }}>{quoteMessage}</p>}
            </form>
        </main>
    );
};

const DetailsPage = ({ request, onBack, user }) => {
    const [quotedParts, setQuotedParts] = useState([]);
    const [partImages, setPartImages] = useState({});
    const [uploading, setUploading] = useState({});
    
    const [generalNotes, setGeneralNotes] = useState('');
    const [quoteMessage, setQuoteMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [deliveryType, setDeliveryType] = useState('No Ofrece');
    const [deliveryCost, setDeliveryCost] = useState('');
    const [deliveryCostBs, setDeliveryCostBs] = useState('');

    useEffect(() => {
        const parts = request.parts_list || [{ part_name: request.partName, quantity: request.quantity || 1 }];
        const initialQuotedParts = parts.map(part => ({
            part_name: part.part_name,
            quantity: part.quantity,
            price: '',
            price_bs: '',
            partBrand: '',
            partType: 'Original',
        }));
        setQuotedParts(initialQuotedParts);
        setPartImages({});
        setUploading({});
    }, [request]);

    const removePartFromQuote = (index) => {
        if (quotedParts.length === 1) {
            alert("Debes cotizar al menos un repuesto. Si no tienes ninguno de los solicitados, es mejor que quites la solicitud de tu panel principal.");
            return;
        }
        
        if (window.confirm(`¿Seguro que quieres quitar "${quotedParts[index].part_name}"? Solo cotizarás los demás repuestos de la lista.`)) {
            const updatedParts = quotedParts.filter((_, i) => i !== index);
            setQuotedParts(updatedParts);
            
            const updatedImages = { ...partImages };
            delete updatedImages[index];
            setPartImages(updatedImages);
        }
    };

    const handlePartChange = (index, field, value) => {
        const updatedParts = [...quotedParts];
        updatedParts[index][field] = value;
        setQuotedParts(updatedParts);
    };

    const handleImageUpload = async (event, index) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        setUploading(prev => ({ ...prev, [index]: true }));
        setQuoteMessage('Subiendo imágenes...');
        
        const part = quotedParts[index];
        const uploadPromises = files.map(file => {
            const filePath = `quotes/${request.id}/${part.part_name.replace(/\s+/g, '_')}/${Date.now()}_${file.name}`;
            const storageRef = ref(storage, filePath);
            return uploadBytes(storageRef, file).then(() => getDownloadURL(storageRef));
        });

        try {
            const downloadURLs = await Promise.all(uploadPromises);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: [...(prevImages[index] || []), ...downloadURLs]
            }));
        } catch (error) {
            console.error("Error al subir imagen: ", error);
            setQuoteMessage("Error al subir una de las imágenes.");
        } finally {
            setUploading(prev => ({ ...prev, [index]: false }));
            setQuoteMessage('');
        }
    };

    const handleRemoveImage = async (index, imageUrl) => {
        if (!window.confirm("¿Seguro que quieres eliminar esta imagen?")) return;
        try {
            const imageRef = ref(storage, imageUrl);
            await deleteObject(imageRef);
            setPartImages(prevImages => ({
                ...prevImages,
                [index]: prevImages[index].filter(url => url !== imageUrl)
            }));
        } catch (error) {
            console.error("Error al eliminar imagen: ", error);
            setQuoteMessage("Error al eliminar la imagen.");
        }
    };

    const handleSendQuote = async (e) => {
        e.preventDefault();

        if (Object.values(uploading).some(status => status === true)) {
            setQuoteMessage("Por favor, espera a que terminen de subirse las imágenes.");
            return;
        }

        setIsLoading(true);

        const businessProfileRef = doc(db, "businesses", user.uid);
        const businessProfileSnap = await getDoc(businessProfileRef);

        if (!businessProfileSnap.exists()) {
            setQuoteMessage('Error: No se pudo encontrar el perfil de tu empresa.');
            setIsLoading(false);
            return;
        }

        const businessProfileData = businessProfileSnap.data();

        const businessDataForQuote = {
            businessSalesTypes: businessProfileData.businessSalesTypes || [],
            business_approximate_location: businessProfileData.business_approximate_location || '',
            business_exact_address: businessProfileData.business_exact_address || '',
            business_name: businessProfileData.business_name || '',
            business_phone: businessProfileData.business_phone || '',
            email: businessProfileData.email || '',
            rif: businessProfileData.rif || '',
            pago_movil: businessProfileData.pago_movil || '',
            binance: businessProfileData.binance || null, // <- BINANCE AÑADIDO AL PAYLOAD
            zelle: businessProfileData.zelle || '',
            international_bank: businessProfileData.international_bank || '',
            transferencia_bs: businessProfileData.transferencia_bs || '',
            efectivo: businessProfileData.efectivo || ''
        };

        for (const part of quotedParts) {
            if (!part.price || !part.partBrand || !part.price_bs) {
                setQuoteMessage(`Por favor, completa los datos para: ${part.part_name}`);
                setIsLoading(false);
                return;
            }
        }

        try {
            const partsToQuote = quotedParts.map((p, index) => ({
                part_name: p.part_name,
                quantity: p.quantity,
                price: parseFloat(p.price) || 0,
                price_bs: parseFloat(p.price_bs) || 0,
                partBrand: p.partBrand,
                partType: p.partType,
                imageUrls: partImages[index] || []
            }));

            const totalPrice = partsToQuote.reduce((sum, part) => sum + part.price, 0);
            const totalPriceBs = partsToQuote.reduce((sum, part) => sum + part.price_bs, 0);

            const quoteData = {
                readableId: request.readableId,
                requestId: request.id,
                businessId: user.uid,
                userId: request.userId,
                createdAt: serverTimestamp(),
                quoteStatus: 'Enviada',
                notes: generalNotes,
                deliveryType: deliveryType,
                deliveryCost: deliveryType === 'Pago' ? parseFloat(deliveryCost || 0) : 0,
                deliveryCostBs: deliveryType === 'Pago' ? parseFloat(deliveryCostBs || 0) : 0,
                partName: partsToQuote.length === 1 ? partsToQuote[0].part_name : `Cotización (${partsToQuote.length} repuestos)`,
                totalPrice: totalPrice,
                totalPriceBs: totalPriceBs,
                quoted_parts: partsToQuote,
                request_type: request.request_type,
                vehicle_details: request.vehicle_details || `${request.brand || ''} ${request.model || ''} ${request.year || ''}`,
                ...businessDataForQuote
            };

            delete quoteData.businessEmail;

            const customQuoteId = `${request.id}_${user.uid}`;
            const quoteRef = doc(db, "quotes", customQuoteId);
            await setDoc(quoteRef, quoteData);

            setQuoteMessage('¡Cotización enviada con éxito!');
            setTimeout(() => onBack(), 2000);
        } catch (error) {
            console.error("Error sending quote: ", error);
            setQuoteMessage('Error al enviar la cotización.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Fecha no disponible';
        return new Date(timestamp.seconds * 1000).toLocaleDateString('es-VE');
    };

    const isFormDisabled = request.status !== 'nueva' && request.status !== 'En revisión';

    return (
        <main className="details-view">
            <button onClick={onBack} className="back-button">&larr; Volver</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h2>Detalle de la Solicitud</h2>
                {request.readableId && (
                    <span style={{ backgroundColor: '#e9ecef', color: '#6c757d', padding: '5px 10px', borderRadius: '8px', fontWeight: 'bold' }}>
                        ID: {request.readableId}
                    </span>
                )}
            </div>
            
            <div className="details-card">
                <p><strong>Vehículo:</strong> {request.brand} {request.model} {request.year}</p>
                <p><strong>Combustible:</strong> {request.fuel_type || 'No especificado'}</p>
                {request.user_approximate_location && <p><strong>Zona de Entrega:</strong> {request.user_approximate_location}</p>}
                <p><strong>Fecha:</strong> {formatDate(request.createdAt)}</p>
            </div>

            {isFormDisabled ? (
                <div className="quote-sent-message">
                    <h3>ℹ️ Esta solicitud ya fue procesada.</h3>
                </div>
            ) : (
            <form onSubmit={handleSendQuote} style={{marginTop: '20px'}}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Enviar Cotización</h3>
                    <p style={{ fontSize: '12px', color: '#6c757d' }}>* Quita los repuestos que no tengas disponibles.</p>
                </div>
                
                {quotedParts.map((part, index) => (
                    <div key={index} className="quote-form-item" style={{ position: 'relative', border: '1px solid #eee', padding: '20px', borderRadius: '8px', marginBottom: '20px', backgroundColor: '#fff' }}>
                        
                        <button 
                            type="button"
                            onClick={() => removePartFromQuote(index)}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: '#fff',
                                color: '#dc3545',
                                border: '1px solid #dc3545',
                                borderRadius: '5px',
                                padding: '2px 8px',
                                cursor: 'pointer',
                                fontSize: '11px',
                                fontWeight: 'bold'
                            }}
                        >
                            ❌ No lo tengo
                        </button>

                        <h4 style={{ color: '#005A9C', marginTop: 0 }}>{part.part_name} (Cant: {part.quantity})</h4>
                        
                        <div className="form-row">
                            <div className="form-group">
                                <label>Precio Total por {part.quantity} Unid. ($)</label>
                                <input type="number" step="0.01" value={part.price} onChange={(e) => handlePartChange(index, 'price', e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label>Precio Total por {part.quantity} Unid. (Bs.)</label>
                                <input type="number" step="0.01" value={part.price_bs} onChange={(e) => handlePartChange(index, 'price_bs', e.target.value)} required />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Marca</label>
                                <input type="text" value={part.partBrand} onChange={(e) => handlePartChange(index, 'partBrand', e.target.value)} required />
                            </div>
                            <div className="form-group">
                                <label>Calidad</label>
                                <select value={part.partType} onChange={(e) => handlePartChange(index, 'partType', e.target.value)}>
                                    <option value="Original">Original</option>
                                    <option value="Genérico Alta Calidad">Genérico Alta Calidad</option>
                                    <option value="Genérico Media Calidad">Genérico Media Calidad</option>
                                    <option value="Genérico Baja Calidad">Genérico Baja Calidad</option>
                                </select>
                            </div>
                        </div>
                        
                        <div className="image-upload-section">
                            <label className="image-upload-label">Fotos del Repuesto</label>
                            <input type="file" multiple accept="image/*" id={`file-input-${index}`} style={{ display: 'none' }} onChange={(e) => handleImageUpload(e, index)} disabled={uploading[index]} />
                            <button type="button" className="upload-btn" onClick={() => document.getElementById(`file-input-${index}`).click()} disabled={uploading[index]}>
                                {uploading[index] ? 'Subiendo...' : '[+] Añadir Fotos'}
                            </button>
                            <div className="thumbnails-container">
                                {(partImages[index] || []).map(imageUrl => (
                                    <div key={imageUrl} className="thumbnail-item">
                                        <img src={imageUrl} alt="Repuesto" />
                                        <button type="button" className="remove-btn" onClick={() => handleRemoveImage(index, imageUrl)}>&times;</button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
                
                <div className="quote-form-item">
                    <h4>Opciones de Delivery</h4>
                    <select value={deliveryType} onChange={(e) => setDeliveryType(e.target.value)}>
                        <option value="No Ofrece">No Ofrece</option>
                        <option value="Gratis">Gratis</option>
                        <option value="Pago">Pago</option>
                    </select>
                    {deliveryType === 'Pago' && (
                        <div className="form-row" style={{ marginTop: '10px' }}>
                            <input type="number" step="0.01" value={deliveryCost} onChange={(e) => setDeliveryCost(e.target.value)} placeholder="Costo $" />
                            <input type="number" step="0.01" value={deliveryCostBs} onChange={(e) => setDeliveryCostBs(e.target.value)} placeholder="Costo Bs." />
                        </div>
                    )}
                </div>

                <div className="form-group">
                    <label>Notas para el cliente</label>
                    <textarea value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} placeholder="Ej: Garantía de 30 días..." />
                </div>

                <button type="submit" disabled={isLoading || Object.values(uploading).some(status => status === true)} style={{ width: '100%', padding: '15px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>
                    {isLoading ? 'Enviando...' : 'ENVIAR COTIZACIÓN'}
                </button>
                {quoteMessage && <p style={{ textAlign: 'center', marginTop: '10px', color: quoteMessage.includes('Error') ? 'red' : 'green' }}>{quoteMessage}</p>}
            </form>
            )}
        </main>
    );
};

const UnifiedDetailsView = ({ item, onBack, user }) => {
    const [enrichedItem, setEnrichedItem] = useState(null);

    useEffect(() => {
    if (item?.id) {
      const unsub = onSnapshot(doc(db, "requests", item.id), async (docSnap) => {
          if (docSnap.exists()) {
              const orderData = docSnap.data();
              
              const userData = {
                fullName: orderData.shipping_receiver_name || "Cliente (verificar envío)",
                phone: orderData.shipping_phone || orderData.userData?.phone,
                documentId: orderData.userDocumentId || "No disponible"
              };

              let quoteData = null;
              if (orderData.winning_quote_id) {
                  const quoteDocRef = doc(db, "quotes", orderData.winning_quote_id);
                  const quoteDocSnap = await getDoc(quoteDocRef);
                  if (quoteDocSnap.exists()) {
                      quoteData = quoteDocSnap.data();
                  }
              }

              setEnrichedItem({ id: docSnap.id, ...orderData, userData, quoteData });
          }
      });
      return () => unsub();
    }
  }, [item]);
    
    const getStatusText = (status) => {
        switch(status) {
            case 'payment_verifying': return 'Verificando Pago';
            case 'preparing_shipment': return 'Preparando Envío';
            case 'ready_for_pickup': return 'Listo para Retirar';
            case 'shipped': return 'Enviado';
            case 'completed': return 'Completado';
            case 'payment_rejected': return 'Pago Rechazado';
            default: return status;
        }
    };

    const handleUpdateStatus = async (newStatus) => {
        if (!enrichedItem?.id) return;
        const orderRef = doc(db, "requests", enrichedItem.id);
        try {
            await updateDoc(orderRef, {
                status: newStatus,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error al actualizar el estado del pedido: ", error);
            alert("Hubo un error al actualizar el estado. Revisa la consola.");
        }
    };

    const renderActionButtons = () => {
        if (!enrichedItem) return null;

        const buttonStyles = {
            padding: '10px 20px',
            fontSize: '16px',
            border: 'none',
            borderRadius: '5px',
            color: 'white',
            cursor: 'pointer',
            marginLeft: '10px',
        };

        const confirmStyle = { ...buttonStyles, backgroundColor: '#28a745' };
        const rejectStyle = { ...buttonStyles, backgroundColor: '#dc3545' };
        const nextStepStyle = { ...buttonStyles, backgroundColor: '#007bff' };
        const completeStyle = { ...buttonStyles, backgroundColor: '#17a2b8' };


        switch (enrichedItem.status) {
            case 'payment_verifying':
                return (
                    <>
                        <button onClick={() => handleUpdateStatus('preparing_shipment')} style={confirmStyle}>Confirmar Pago</button>
                        <button onClick={() => handleUpdateStatus('payment_rejected')} style={rejectStyle}>Rechazar Pago</button>
                    </>
                );
            case 'preparing_shipment':
                const nextStatus = enrichedItem.shipping_address ? 'shipped' : 'ready_for_pickup';
                const buttonText = enrichedItem.shipping_address ? 'Enviar Paquete' : 'Marcar como Listo para Retiro';
                return (
                    <button onClick={() => handleUpdateStatus(nextStatus)} style={nextStepStyle}>{buttonText}</button>
                );
            case 'shipped':
            case 'ready_for_pickup':
                return (
                    <button onClick={() => handleUpdateStatus('completed')} style={completeStyle}>Marcar como Completado</button>
                );
            default:
                return null;
        }
    };

    if (!enrichedItem) {
        return <p>Cargando detalles...</p>;
    }

    const order = enrichedItem;
    
    const digitalPaymentMethods = ['pago_movil', 'zelle', 'transferencia_bs', 'international_bank'];
    const showCustomerData = !digitalPaymentMethods.includes(order.payment_method) || !['payment_verifying', 'payment_rejected'].includes(order.status);
    
    let subtotalUSD = 0, subtotalBs = 0, deliveryCostUSD = 0, deliveryCostBs = 0;
    let totalUSD = order.total_price || 0;
    let totalBs = order.total_price_bs || 0;
    let hasBreakdown = false;

    if (order.quoteData) {
        hasBreakdown = true;
        deliveryCostUSD = order.quoteData.deliveryCost || 0;
        deliveryCostBs = order.quoteData.deliveryCostBs || 0;
        
        if (order.quoteData.totalPrice !== undefined) {
            subtotalUSD = order.quoteData.totalPrice;
            subtotalBs = order.quoteData.totalPriceBs || 0;
        } else {
            subtotalUSD = totalUSD - deliveryCostUSD;
            subtotalBs = totalBs - deliveryCostBs;
        }
    }
    
    return (
        <main className="details-view">
            <button onClick={onBack} className="back-button">&larr; Volver</button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Detalle de la Venta</h2>
                <span style={{
                    backgroundColor: '#e9ecef',
                    color: '#495057',
                    padding: '5px 10px',
                    borderRadius: '8px',
                    fontWeight: 'bold'
                }}>
                    ID: {order.readableId || order.id}
                </span>
            </div>
            <p><strong>Estado:</strong> {getStatusText(order.status)}</p>
            <div className="order-card-details" style={{marginTop: '20px', border: 'none', padding: 0}}>
                <div className="details-section">
                    <h4>Información del Vehículo</h4>
                    <p><strong>Tipo:</strong> {order.vehicle_type || 'No especificado'}</p>
                    <p><strong>Marca:</strong> {order.brand || 'No especificado'}</p>
                    <p><strong>Modelo:</strong> {order.model || 'No especificado'}</p>
                    <p><strong>Año:</strong> {order.year || 'No especificado'}</p>
                    <p><strong>Combustible:</strong> {order.fuel_type || 'No especificado'}</p>
                </div>
                <div className="details-section">
                    <h4>Desglose del Pedido</h4>
                    {order.quoteData && order.quoteData.quoted_parts ? (
                        order.quoteData.quoted_parts.map((part, index) => (
                            <p key={index}>
                                - {part.part_name} <strong>({part.partBrand})</strong> (Cantidad: {part.quantity})
                            </p>
                        ))
                    ) : (
                        (order.parts_list || [{ part_name: order.partName, quantity: order.quantity || 1 }]).map((part, index) => (
                            <p key={index}>- {part.part_name} (Cantidad: {part.quantity})</p>
                        ))
                    )}
                </div>

                {showCustomerData ? (
                  <>
                    <div className="details-section">
                        <h4>Detalles del Cliente</h4>
                        <p><strong>Nombre:</strong> {order.userData.fullName}</p>
                        <p><strong>Teléfono:</strong> {order.userData.phone || 'No disponible'}</p>
                        <p><strong>Cédula/ID:</strong> {order.userData.documentId || 'No disponible'}</p>
                    </div>

                    {order.shipping_address && (
                        <div className="details-section">
                            <h4>Dirección de Envío</h4>
                            <p>{order.shipping_receiver_name}</p>
                            <p>{order.shipping_address}, {order.shipping_city}, {order.shipping_state}</p>
                            <p>Tel: {order.shipping_phone}</p>
                        </div>
                    )}
                  </>
                ) : (
                  <div className="details-section">
                      <h4>Detalles del Cliente y Envío</h4>
                      <p style={{color: '#6c757d', fontStyle: 'italic'}}>La información de contacto y envío se mostrará una vez que el pago sea confirmado.</p>
                  </div>
                )}
                
                <div className="details-section">
                    <h4>Detalles del Pago</h4>
                    <p><strong>Método:</strong> {order.payment_method}</p>
                    <p><strong>Referencia:</strong> {order.payment_reference || 'N/A (Efectivo)'}</p>
                    
                    {hasBreakdown ? (
                        <>
                            <p><strong>Subtotal Repuestos:</strong> ${subtotalUSD.toFixed(2)} / Bs. {subtotalBs.toFixed(2)}</p>
                            <p><strong>Costo de Envío:</strong> {deliveryCostUSD > 0 || deliveryCostBs > 0 ? `$${deliveryCostUSD.toFixed(2)} / Bs. ${deliveryCostBs.toFixed(2)}` : 'Gratis / No aplica'}</p>
                            <hr style={{margin: '8px 0'}}/>
                            <p><strong>Total Pagado:</strong> ${totalUSD.toFixed(2)} / Bs. {totalBs.toFixed(2)}</p>
                        </>
                    ) : (
                        <>
                            <p><strong>Total Pagado ($):</strong> ${totalUSD.toFixed(2)}</p>
                            <p><strong>Total Pagado (Bs.):</strong> Bs. {totalBs.toFixed(2)}</p>
                        </>
                    )}
                </div>
            </div>
            
            <div className="order-actions" style={{ padding: '20px 0', textAlign: 'right', borderTop: '1px solid #eee', marginTop: '20px' }}>
                {renderActionButtons()}
            </div>

        </main>
    );
};

export default App;